import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { ArrowLeft, CalendarIcon, Building2, Search, User } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { SERVICE_TYPES, REMINDER_OPTIONS } from '../mock';
import { branchesAPI, servicesAPI, appointmentsAPI } from '../services/api';

const AppointmentForm = ({ appointment, onSave, onCancel }) => {
  console.log('[AppointmentForm] COMPONENT MOUNTED/RE-RENDERED with appointment:', appointment);
  
  // Initialize with today's date (local time, not UTC)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    return today;
  });
  
  const [formData, setFormData] = useState(() => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayFormatted = `${year}-${month}-${day}`;
    
    return {
      serviceType: '',
      patientName: '',
      phone: '',
      date: todayFormatted, // Initialize with today's date
      time: '',
      reason: '',
      status: 'pending',
      reminderTime: 120, // Default to 2 hours
      branchId: ''
    };
  });
  const [customServiceType, setCustomServiceType] = useState('');
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  
  // Patient search state
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingPatients, setSearchingPatients] = useState(false);

  // Define loader functions first
  const loadBranches = async () => {
    try {
      const response = await branchesAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    }
  };

  // Load branches and services on mount
  useEffect(() => {
    console.log('[AppointmentForm] EFFECT 1: Loading services and branches');
    const loadData = async () => {
      await loadBranches();
      await loadServices();
      setServicesLoaded(true);
      console.log('[AppointmentForm] EFFECT 1: Services loaded');
    };
    loadData();
  }, []);

  // Sync selectedDate with formData.date on initial mount if no appointment (new appointment)
  useEffect(() => {
    if (!appointment) {
      // Always ensure formData.date is set to today's date when creating new appointment
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      console.log('[AppointmentForm] EFFECT: Ensuring date is set for new appointment:', formattedDate);
      
      // Update both selectedDate and formData.date to be in sync
      const dateObj = new Date(year, today.getMonth(), today.getDate(), 12, 0, 0);
      setSelectedDate(dateObj);
      setFormData(prev => ({ ...prev, date: formattedDate }));
    }
  }, [appointment]); // Re-run if appointment changes

  // Process appointment data when component mounts or appointment changes
  useEffect(() => {
    console.log('[AppointmentForm] EFFECT 2: Triggered with appointment:', appointment);
    console.log('[AppointmentForm] EFFECT 2: servicesLoaded:', servicesLoaded);
    
    if (!appointment) {
      console.log('[AppointmentForm] EFFECT 2: No appointment, skipping');
      return;
    }
    
    // Check if this is just a preselected date from CalendarView (not a real appointment)
    const isPreselectedDateOnly = appointment.date && !appointment.patientName && !appointment._id && !appointment.id;
    
    if (isPreselectedDateOnly) {
      console.log('[AppointmentForm] EFFECT 2: This is just a preselected date, setting only date');
      // Only set the date, keep other fields empty for new appointment
      setFormData(prev => ({ ...prev, date: appointment.date }));
      
      // Parse and set selectedDate
      const dateParts = appointment.date.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[2], 10);
      const dateObj = new Date(year, month, day, 12, 0, 0, 0);
      setSelectedDate(dateObj);
      return;
    }
    
    if (!servicesLoaded) {
      console.log('[AppointmentForm] EFFECT 2: Services not loaded yet, will retry when loaded');
      return;
    }
    
    console.log('[AppointmentForm] EFFECT 2: Processing appointment data');
    console.log('[AppointmentForm] Available services:', services.map(s => s.name));
    
    // Get the serviceType from appointment
    const appointmentServiceType = appointment.serviceType || appointment.service || '';
    console.log('[AppointmentForm] Appointment serviceType:', appointmentServiceType);
    
    // Check if serviceType exists in loaded services or default types
    const existsInServices = services.some(s => s.name === appointmentServiceType);
    const existsInDefaults = SERVICE_TYPES.includes(appointmentServiceType);
    
    console.log('[AppointmentForm] Service exists in custom services:', existsInServices);
    console.log('[AppointmentForm] Service exists in default types:', existsInDefaults);
    
    let finalServiceType = appointmentServiceType;
    let finalCustomServiceType = '';
    
    // If serviceType doesn't exist in available options, treat it as custom
    if (appointmentServiceType && !existsInServices && !existsInDefaults) {
      console.log('[AppointmentForm] ServiceType not found in options, treating as custom');
      finalServiceType = 'Otro';
      finalCustomServiceType = appointmentServiceType;
    }
    
    // Populate all form fields with appointment data
    const appointmentData = {
      id: appointment.id || appointment._id, // Include ID for updates
      _id: appointment._id || appointment.id, // Include _id for updates
      serviceType: finalServiceType,
      patientName: appointment.patientName || '',
      phone: appointment.phone || '',
      date: appointment.date || '',
      time: appointment.time || '',
      reason: appointment.reason || '',
      status: appointment.status || 'pending',
      reminderTime: appointment.reminderTime || 120,
      branchId: appointment.branchId || '',
      tags: appointment.tags || [] // Include tags to preserve them on edit
    };
    
    console.log('[AppointmentForm] EFFECT 2: Setting form data:', appointmentData);
    console.log('[AppointmentForm] EFFECT 2: Custom service type:', finalCustomServiceType);
    
    setFormData(appointmentData);
    setCustomServiceType(finalCustomServiceType);
    
    if (appointment.date) {
      // Parse date as local time, not UTC
      // If date is "2025-11-04", split and create local date
      const dateParts = appointment.date.split('-');
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);
      const dateObj = new Date(year, month, day, 12, 0, 0, 0);
      setSelectedDate(dateObj);
      console.log('[AppointmentForm] EFFECT 2: Set selected date (local):', dateObj, 'from string:', appointment.date);
    }
    
    console.log('[AppointmentForm] EFFECT 2: Completed');
  }, [appointment, servicesLoaded, services]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('[AppointmentForm] SUBMIT - formData at submit:', formData);
    console.log('[AppointmentForm] SUBMIT - formData.date value:', formData.date);
    console.log('[AppointmentForm] SUBMIT - formData.date type:', typeof formData.date);
    
    // Validate required fields
    if (!formData.serviceType) {
      alert('Por favor selecciona un tipo de servicio');
      return;
    }
    if (formData.serviceType === 'Otro' && !customServiceType.trim()) {
      alert('Por favor especifica el tipo de servicio personalizado');
      return;
    }
    if (!formData.patientName) {
      alert('Por favor ingresa el nombre del paciente');
      return;
    }
    if (!formData.phone) {
      alert('Por favor ingresa el número de teléfono');
      return;
    }
    if (!formData.date) {
      console.log('[AppointmentForm] SUBMIT - Date validation FAILED. formData.date is empty or falsy');
      alert('Por favor selecciona una fecha');
      return;
    }
    if (!formData.time) {
      alert('Por favor selecciona una hora');
      return;
    }
    if (!formData.reason) {
      alert('Por favor ingresa el motivo de consulta');
      return;
    }
    
    // Prepare final data - if serviceType is "Otro", use the custom value
    const finalData = {
      ...formData,
      serviceType: formData.serviceType === 'Otro' ? customServiceType.trim() : formData.serviceType
    };
    
    console.log('[AppointmentForm] Submitting form with data:', finalData);
    onSave(finalData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      // Format date manually to avoid timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      console.log('[AppointmentForm] Date selected:', formattedDate, 'from Date object:', date);
      console.log('[AppointmentForm] Calling handleChange with date:', formattedDate);
      handleChange('date', formattedDate);
      console.log('[AppointmentForm] After handleChange, formData should have date:', formattedDate);
    } else {
      console.log('[AppointmentForm] Date selected is null or undefined');
    }
  };

  console.log('[AppointmentForm] RENDER - Current formData:', formData);
  console.log('[AppointmentForm] RENDER - servicesLoaded:', servicesLoaded);
  console.log('[AppointmentForm] RENDER - services count:', services.length);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          onClick={onCancel}
          variant="ghost"
          className="mb-4 hover:bg-blue-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-t-lg">
            <CardTitle className="text-2xl">
              {appointment ? 'Editar Cita' : 'Nueva Cita'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="serviceType">Tipo de Servicio *</Label>
                <Select 
                  value={formData.serviceType} 
                  onValueChange={(value) => {
                    handleChange('serviceType', value);
                    if (value !== 'Otro') {
                      setCustomServiceType('');
                    }
                  }}
                >
                  <SelectTrigger id="serviceType" className="focus:ring-2 focus:ring-blue-500">
                    <SelectValue placeholder="Selecciona el tipo de servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.length > 0 ? (
                      <>
                        {services.map(service => (
                          <SelectItem key={service.id} value={service.name}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{backgroundColor: service.color}}
                              />
                              {service.name}
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="Otro">Otro (personalizado)</SelectItem>
                      </>
                    ) : (
                      <>
                        {SERVICE_TYPES.map(type => (
                          <SelectItem key={`service-${type}`} value={type}>{type}</SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {services.length === 0 && (
                  <p className="text-xs text-gray-500">
                    💡 Tip: Puedes crear servicios personalizados en tu Perfil → Servicios
                  </p>
                )}
              </div>

              {formData.serviceType === 'Otro' && (
                <div className="space-y-2">
                  <Label htmlFor="customServiceType">Especifica el servicio *</Label>
                  <Input
                    id="customServiceType"
                    value={customServiceType}
                    onChange={(e) => {
                      setCustomServiceType(e.target.value);
                      // Don't update formData.serviceType here - keep it as "Otro"
                      // The actual value will be used in handleSubmit
                    }}
                    placeholder="Ej: Cirugía, Diagnóstico, etc."
                    required
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {branches.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="branchId">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Sucursal
                  </Label>
                  <Select 
                    value={formData.branchId || 'none'} 
                    onValueChange={(value) => handleChange('branchId', value === 'none' ? null : value)}
                  >
                    <SelectTrigger id="branchId" className="focus:ring-2 focus:ring-blue-500">
                      <SelectValue placeholder="Selecciona una sucursal (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin sucursal</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{backgroundColor: branch.color}}
                            />
                            {branch.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Asocia esta cita con una sucursal específica
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="patientName">Nombre *</Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="patientName"
                      type="text"
                      name="patient-name"
                      value={formData.patientName}
                      onChange={async (e) => {
                        const value = e.target.value;
                        handleChange('patientName', value);
                        
                        // Search for existing patients
                        if (value.length >= 2) {
                          setSearchingPatients(true);
                          try {
                            const response = await appointmentsAPI.searchPatients(value);
                            setPatientSuggestions(response.data || []);
                            setShowSuggestions(true);
                          } catch (error) {
                            console.error('Error searching patients:', error);
                            setPatientSuggestions([]);
                          }
                          setSearchingPatients(false);
                        } else {
                          setPatientSuggestions([]);
                          setShowSuggestions(false);
                        }
                      }}
                      onFocus={() => {
                        if (patientSuggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay hiding to allow click on suggestion
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                      placeholder="Buscar o escribir nombre..."
                      required
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-form-type="other"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      inputMode="text"
                      className="pl-10 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Patient suggestions dropdown */}
                  {showSuggestions && patientSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2 text-xs text-gray-500 border-b bg-gray-50">
                        Pacientes anteriores
                      </div>
                      {patientSuggestions.map((patient, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center justify-between border-b last:border-b-0"
                          onClick={() => {
                            handleChange('patientName', patient.patientName);
                            handleChange('phone', patient.phone);
                            setShowSuggestions(false);
                            setPatientSuggestions([]);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <div>
                              <div className="font-medium text-gray-900">{patient.patientName}</div>
                              <div className="text-sm text-gray-500">{patient.phone}</div>
                            </div>
                          </div>
                          {patient.lastVisit && (
                            <span className="text-xs text-gray-400">
                              Última cita: {patient.lastVisit}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searchingPatients && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">Escribe para buscar pacientes anteriores o agrega uno nuevo</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Número de Teléfono (WhatsApp) *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Ej: +34612345678"
                  required
                  className="focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500">Incluye el código de país (ej: +34 para España)</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCalendar(!showCalendar)}
                    className={cn(
                      'w-full justify-start text-left font-normal focus:ring-2 focus:ring-blue-500',
                      !selectedDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP', { locale: es }) : 'Selecciona una fecha'}
                  </Button>
                  
                  {showCalendar && (
                    <div className="mt-2 border rounded-lg p-2 bg-white shadow-lg">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          handleDateSelect(date);
                          setShowCalendar(false);
                        }}
                        locale={es}
                        initialFocus
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time">Hora *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleChange('time', e.target.value)}
                    required
                    className="focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo de Consulta / Servicio *</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => handleChange('reason', e.target.value)}
                  placeholder="Describe el motivo de la consulta o servicio solicitado"
                  required
                  rows={4}
                  className="focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Estado</Label>
                <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                  <SelectTrigger id="status" className="focus:ring-2 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="status-pending" value="pending">Pendiente</SelectItem>
                    <SelectItem key="status-confirmed" value="confirmed">Confirmada</SelectItem>
                    <SelectItem key="status-cancelled" value="cancelled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminderTime">
                  🔔 Recordarme
                </Label>
                <Select 
                  value={formData.reminderTime?.toString()} 
                  onValueChange={(value) => handleChange('reminderTime', parseInt(value))}
                >
                  <SelectTrigger id="reminderTime" className="focus:ring-2 focus:ring-blue-500">
                    <SelectValue placeholder="Selecciona cuándo recordar" />
                  </SelectTrigger>
                  <SelectContent>
                    {REMINDER_OPTIONS.map(option => (
                      <SelectItem key={`reminder-${option.value}`} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Recibirás una notificación en este tiempo antes de la cita
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {appointment ? 'Actualizar Cita' : 'Crear Cita'}
                </Button>
                <Button
                  type="button"
                  onClick={onCancel}
                  variant="outline"
                  className="flex-1 py-6"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppointmentForm;